import {
  ContactSearchField,
  ContactSearchSelection,
} from '@/components/ContactSearchField/ContactSearchField';
import { TypesenseSearchMode } from '@/lib/typesense/types';
import {
  Button,
  Collapse,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  Building2Icon,
  ChevronRightIcon,
  PlusCircleIcon,
  UserPlusIcon,
} from 'lucide-react';
import CollapseButton from './CollapseButton';
import styles from './AddContactPanel.module.css';

const AddContactPanel = ({
  panelTitle: formTitle,
  searchFieldLabel,
  searchFieldPlaceholder,
  searchMode,
  onContactSelection,
  getAlreadyAddedContactIds,
  isCollapsed,
  onToggleCollapse,
  collapsedAccessibilityLabel,
  expandedAccessibilityLabel,
}: {
  panelTitle: string;
  searchFieldLabel: string;
  searchFieldPlaceholder: string;
  searchMode: TypesenseSearchMode;
  onContactSelection: (selection: ContactSearchSelection) => void;
  getAlreadyAddedContactIds: () => string[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  collapsedAccessibilityLabel: string;
  expandedAccessibilityLabel: string;
}) => {
  const handleCreatePerson = () => {
    onContactSelection({
      type: 'newHuman',
      id: 'new',
      query: '',
    });
  };

  const handleCreateCompany = () => {
    onContactSelection({
      type: 'newOrganization',
      id: 'new',
      query: '',
    });
  };

  return (
    <CollapsibleContactCard
      title={formTitle}
      collapsedAccessibilityLabel={collapsedAccessibilityLabel}
      expandedAccessibilityLabel={expandedAccessibilityLabel}
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
      childrenWhenExpanded={
        <Stack gap='md' p='md'>
          <Stack gap='1px'>
            <Text size='sm' fw={500}>
              {searchFieldLabel}
            </Text>

            <ContactSearchField
              mode={searchMode}
              accessibilityTitle={searchFieldPlaceholder}
              onSelect={onContactSelection}
              excludeContacts={getAlreadyAddedContactIds()}
              autoFocus={true}
              isDisabled={false}
            />
          </Stack>

          <Divider
            label={
              <Text size='sm' fw={500}>
                or
              </Text>
            }
            labelPosition='center'
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'space-between',
              rowGap: '0.5rem',
              columnGap: '1rem',
              flexWrap: 'wrap',
            }}
          >
            {searchMode !== 'organization' && (
              <Button
                leftSection={<UserPlusIcon size={16} />}
                variant='outline'
                style={{ flex: '1 1 0' }}
                onClick={handleCreatePerson}
              >
                Create new person
              </Button>
            )}
            {searchMode !== 'human' && (
              <Button
                leftSection={<Building2Icon size={16} />}
                variant='outline'
                style={{ flex: '1 1 0' }}
                onClick={handleCreateCompany}
              >
                Create new company
              </Button>
            )}
          </div>
        </Stack>
      }
    />
  );
};

export default AddContactPanel;

export const CollapsibleContactCard = ({
  title,
  collapsedAccessibilityLabel,
  expandedAccessibilityLabel,
  childrenWhenCollapsed,
  childrenWhenExpanded,
  isCollapsed,
  onToggleCollapse,
}: {
  title: string;
  collapsedAccessibilityLabel: string;
  expandedAccessibilityLabel: string;
  childrenWhenCollapsed?: React.ReactNode;
  childrenWhenExpanded: React.ReactNode;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}) => {
  const transitionDuration = 200;
  return (
    <Paper
      role={isCollapsed ? 'button' : 'region'}
      aria-expanded={!isCollapsed}
      aria-label={
        isCollapsed ? collapsedAccessibilityLabel : expandedAccessibilityLabel
      }
      withBorder
      radius='xl'
      py='xs'
      px='md'
      className={`${styles.collapsibleContactCard} ${
        isCollapsed ? styles.collapsed : ''
      }`}
      onClick={() => isCollapsed && onToggleCollapse()}
    >
      <Header
        title={title}
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
      />
      {childrenWhenCollapsed && (
        <Collapse in={isCollapsed} transitionDuration={transitionDuration}>
          {childrenWhenCollapsed}
        </Collapse>
      )}
      <Collapse in={!isCollapsed} transitionDuration={transitionDuration}>
        {childrenWhenExpanded}
      </Collapse>
    </Paper>
  );
};

const Header = ({
  title,
  isCollapsed,
  onToggleCollapse,
}: {
  title: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}) => {
  return (
    <Group gap='md'>
      <CollapseButton
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
        collapsedIcon={<PlusCircleIcon size='16' strokeWidth={1.5} />}
        expandedIcon={<ChevronRightIcon size='16' strokeWidth={1.5} />}
      />

      <Title
        order={3}
        size='h4'
        fw={600}
        style={{ margin: '0', color: 'var(--mantine-color-text)' }}
      >
        {title}
      </Title>
    </Group>
  );
};
